import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    getUserEventBookingsByUserId,
    createOrUpdateOrDismissReview,
    deleteEventReviewById
} from "../../../../../../app/store/slices/user/event-bookings/eventBookings";

/**
 * TODO:
 * 
 * 1. Do not show ratings key and value when active tab is 'upcoming' or 'active'
 * 2. In elipsis menu, hide 'Give Ratings' option when active tab is 'upcoming' or 'active'.
 * 3. If user already given ratings for that event booking, replace the text with "Edit Ratings" option in elipsis menu.
 * 4. Combine two columns of charges in tax-invoice/TicketBookingInvoice.pdf and write only "Charges" as header name and in value pass % + Rupee charges both. if any of these is not applicable for that ticket type, pass only one value.
 * 5. Add 3 rows in payment summary for platform fees in same pdf as above. only if applicable.
 * 6. Change the ticket name with description and only add two rows. 1. Service Fee, 2. Platform Commission
 */
export default function useEventBookings() {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { user, isLoggedIn } = useSelector((state) => state.auth);
    const { loading, userBookings, userReviews } = useSelector((state) => state.eventBookings);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('');

    const [activeTab, setActiveTab] = useState('upcoming');

    const [openPendingRatingsDrawer, setOpenPendingRatingsDrawer] = useState(false);

    const [openEventReviewModal, setOpenEventReviewModal] = useState(false);

    /**
     * InvoiceModal related states
     */
    const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
    const [invoiceModalType, setInvoiceModalType] = useState('creatorToUserInvoice'); // 'creatorToUserInvoice' | 'shmoozToUserInvoice' | 'purchaseTicket'

    const filterByStatus = [
        {
            label: 'Event Cancelled',
            value: 'eventCancelled'
        },
        {
            label: 'Refunded',
            value: 'refunded'
        },
        // {
        //     label: 'Ticket Booked',
        //     value: 'ticketBooked'
        // },
        {
            label: 'Refund Initiated',
            value: 'refundInitiated'
        }
    ]

    const menusList = [
        {
            id: 'viewTicketInvoice',
            menuName: 'Ticket Invoice',
            onClick: () => handleInvoiceModal('creatorToUserInvoice')
        },
        {
            id: 'viewBookingInvoice',
            menuName: 'Booking Invoice',
            onClick: () => handleInvoiceModal('shmoozToUserInvoice')
        },
        ...(activeTab === 'past'
            ? [
                {
                    id: 'giveFeedback',
                    menuName: 'Give Ratings',
                    onClick: onGiveFeedbackClick
                }
            ]
            : [])
    ];

    const filteredMyEventBookings = useMemo(() => {
        let bookings = userBookings;

        if (bookings.length === 0) return bookings;

        // Filter by search term
        if (searchTerm) {
            bookings = bookings.filter((booking) =>
                booking?.eventTitle?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by selected status
        // if (selectedFilter) {
        //     bookings = bookings.filter(
        //         (booking) => booking?.status?.toLowerCase() === selectedFilter.toLowerCase()
        //     );
        // }

        // if (activeTab === 'upcoming') {
        //     bookings = bookings.filter(
        //         (booking) => new Date(booking?.eventDate) >= new Date()
        //     );
        // }

        // if (activeTab === 'past') {
        //     bookings = bookings.filter(
        //         (booking) => new Date(booking?.eventDate) < new Date()
        //     );
        // }

        return bookings;
    }, [searchTerm, selectedFilter, activeTab, userBookings]);

    function onElipsisMenuClose() {
        setAnchorEl(null);
    }

    function onElipsisMenuOpen(event) {
        setAnchorEl(event.currentTarget);
    }

    function onExploreEventsClick() {
        navigate('/events');
    }

    function handleInvoiceModal(type) {
        setInvoiceModalType(type);
        setOpenInvoiceModal(true);
    }

    function onViewTicketClick() {
        handleInvoiceModal('purchaseTicket');
    }

    function onInvoiceModalClose() {
        setOpenInvoiceModal(false);
    }

    function onInvoiceDownload() {
        setOpenInvoiceModal(false);
    }

    function onGiveFeedbackClick() {
        setOpenEventReviewModal(!openEventReviewModal);
    }


    return {
        filterByStatus,
        anchorEl,
        open,
        onElipsisMenuClose,
        onElipsisMenuOpen,
        setSearchTerm,
        setSelectedFilter,
        menusList,
        filteredMyEventBookings,
        activeTab,
        setActiveTab,
        openPendingRatingsDrawer,
        setOpenPendingRatingsDrawer,
        onExploreEventsClick,
        openEventReviewModal,
        onGiveFeedbackClick,
        onViewTicketClick,
        openInvoiceModal,
        invoiceModalType,
        onInvoiceDownload,
        onInvoiceModalClose,
        loading,
    };
}

// getUserBookings
// getUserTicket (Individual Ticket Details)
// getUserBookingInvoice
// getUserPurchaseTicketTaxInvoice

// post -> generateUserBookingInvoicePDF
// post -> generateUserPurchaseTicketPDF
// post -> generateUserPurchaseTicketPDF

// post -> submitEventReviews
// put  -> updateEventReviews
// post -> dismissEventReview


const creatorToUserInvoice = {
    invoiceTo: {
        username: 'John Doe',
        email: 'john.doe@example.com',
        phone: '9898989898',
        state: 'California',
    },
    issuedBy: {
        eventCreatorName: 'Event Creator Inc.',
        gstNumber: '27AAEPM1234C1Z5',
        city: 'Los Angeles',
        state: 'California',
    },
    invoiceDetails: {
        invoiceNumber: 'INV-1001',
        dateOfIssue: '2023-10-01',
        sacCode: '998314',
        serviceDescription: 'Online Event Ticketing Services',
        placeOfSupply: 'California',
        isReverseChargeApplicable: false,
    },
    eventDetails: {
        eventName: 'Tech Conference 2023',
        venue: {
            venueName: 'Convention Center',
            latitude: '34.0522',
            longitude: '118.2437',
        },
        eventDate: '2023-11-15',
        eventTime: '10:00 AM - 5:00 PM',
    },
    bookingDetails: {
        bookingID: 'BK-5678',
        bookedOn: '2023-09-25',
        noOfTickets: 2,
    },
    paymentDetails: {
        transactionID: 'TXN-7890',
        paymentMethod: 'Credit Card',
        amountPaid: 150.00,
        paidOn: '2023-09-25',
        amountPaidInWords: 'One Hundred Fifty Rupees Only',
    },
    paymentSummary: {
        subTotalExcludingTax: 127.12,
        totalTaxAmount: 22.88,
        totalAmountIncludingTax: 150.00,
        amountPaidInWords: 'One Hundred Fifty Rupees Only',
        gstBreakdown: [
            {
                taxRate: '18%',
                taxAmount: 22.88,
                taxName: 'IGST',
            }
        ]
    },
    ticketDetails: {
        tickets: [
            {
                ticketType: 'General Admission',
                price: 75.00,
                qty: 2,
                gstRate: '18%',
                gstAmount: 22.88,
                totalAmountIncludingTax: 200
            }
        ],
        grossTotal: 150.00,
    }
}

const shmoozToUserInvoice = {
    invoiceTo: {
        username: 'John Doe',
        email: 'john.doe@example.com',
        phone: '9898989898',
        state: 'California',
    },
    issuedBy: {
        issuerName: 'Shmooz Social LLP',
        gstNumber: '27AAEPM1234C1Z5',
        city: 'San Francisco',
        state: 'California',
        address: '123 Market St, San Francisco, CA 94103',
    },
    invoiceDetails: {
        invoiceNumber: 'SHMZ-INV-2002',
        dateOfIssue: '2023-10-01',
        sacCode: '998314',
        serviceDescription: 'Event Booking Facilitation Services',
        placeOfSupply: 'California',
        isReverseChargeApplicable: false,
    },
    eventDetails: {
        eventName: 'Tech Conference 2023',
        venue: {
            venueName: 'Convention Center',
            latitude: '34.0522',
            longitude: '118.2437',
        },
        eventDate: '2023-11-15',
        eventTime: '10:00 AM - 5:00 PM',
        eventCreatorName: 'Event Creator Inc.',
    },
    bookingDetails: {
        bookingID: 'BK-5678',
        bookedOn: '2023-09-25',
        noOfTickets: 2,
    },
    paymentBreakup: {
        bookingFees: {
            tickets: [
                {
                    ticketType: 'General Admission',
                    price: 75.00,
                    qty: 2,
                    chargesEachQty: 5.00,
                    totalCharges: 10.00,
                }
            ],
            bookingFeesExlusiveOfTax: 10.00,
            taxBreakdown: [
                {
                    taxRate: '18%',
                    taxAmount: 1.80,
                    taxName: 'IGST',
                }
            ],
            totalBookingFeesInclusiveOfTax: 11.80,
        },
        platformFees: {
            feesExlusiveOfTax: 5.00,
            tickets: [
                {
                    ticketType: 'General Admission',
                    price: 75.00,
                    qty: 2,
                    percentageCharges: 2.5,
                    fixedRupeeCharges: 2.50,
                    totalCharges: 5.00,
                }
            ],
            taxBreakdown: [
                {
                    taxRate: '18%',
                    taxAmount: 0.90,
                    taxName: 'IGST',
                }
            ],
            totalPlatformFeesInclusiveOfTax: 5.90,
        },
        paymentSummary: {
            totalAmiuntExcludingTax: 27.00,
            totalTaxAmount: 2.70,
            totalAmountIncludingTax: 29.70,
            amountPaidInWords: 'Twenty Nine Rupees and Seventy Paise Only',
            taxBreakdown: [
                {
                    taxRate: '18%',
                    taxAmount: 2.70,
                    taxName: 'IGST',
                }
            ]
        },
        paymentDetails: {
            transactionID: 'TXN-7890',
            paymentMethod: 'Credit Card',
            amountPaid: 29.70,
            paidOn: '2023-09-25',
            amountPaidInWords: 'Twenty Nine Rupees and Seventy Paise Only',
        }
    }
};

const purchaseTicket = {
    eventName: 'Tech Conference 2023',
    attendeeName: 'John Doe',
    location: 'Convention Center, Los Angeles, CA',
    ticketName: 'General Admission',
    eventCreatorName: 'Event Creator Inc.',
    ticketId: 'TCKT-1234',
    qrCodeImage: 'base64',
    ticketValidFor: [
        {
            date: '2023-11-15',
            startTime: '10:00 AM',
            endTime: '5:00 PM',
        }
    ]
}

const getUserBookings = [
    {
        eventDetails: {
            id: 'EV-1234',
            title: 'Tech Conference 2023',
            slug: 'tech-conference-2023',
            ratings: 4.5,
            eventCreator: 'Event Creator Inc.',
            location: {
                venueName: 'Convention Center',
                latitude: '34.0522',
                longitude: '118.2437',
            },
            thumbnailUrl: 'https://example.com/event-thumbnail.jpg',
            startDate: '2023-11-15',
            endDate: '2023-11-16',
            startTime: '10:00 AM',
            endTime: '5:00 PM',
            eventType: 'singleDay',
            schedules: []
        },
        bookingDetails: {
            id: 'BK-5678',
            status: 'ticketBooked',
            bookedOn: '2023-09-25',
            bookedBy: 'John Doe',
            amountPaid: 150.00,
            attendees: [
                {
                    attendeeName: 'John Doe',
                    attendeeEmail: 'john.doe@example.com',
                    attendeePhone: '9898989898',
                    ticketId: 'TICKT-1234',
                }
            ]
        },
        isUserGivenRating: false,
    }
]